/**
 * The **Vacuum pickup** sprite - the client face of one arena `pickup` entity of
 * kind `vacuum` (PRD #431; sim half in `src/sim/xpGem.ts`). A rare elite/boss
 * drop that, when the runner claims it, hoovers the whole gem field into them at
 * once. It must read "special" **across the field** - deliberately *not* a gem
 * tier (no green/blue/violet/gold): a bright white-cyan pulsing starburst with a
 * slow rotating glow, drawn from Graphics primitives so it degrades gracefully
 * with no baked art (the moongate's Graphics-fallback convention).
 *
 * Pure, deterministic motion (a wall-clock-free pulse driven by the render dt),
 * so it unit-tests headlessly like {@link ./xpOrbSprite.XpOrbSprite}.
 */
import { Container, Graphics } from 'pixi.js';

/** The pulse period (ms) of the slow "special pickup" breathing. */
const PULSE_PERIOD_MS = 1100;

/** Slow rotation period (ms) of the starburst, so it shimmers as it hangs. */
const SPIN_PERIOD_MS = 4200;

/** The vacuum's signature colour - a bright cyan-white, unmistakably not a gem
 * tier. Exposed for the renderer's grab burst. */
export const VACUUM_COLOR = 0xdff6ff;

/** Points on the starburst. */
const STAR_POINTS = 6;

/** Base outer radius of the star (px), before the pulse. */
const STAR_RADIUS = 9;

/**
 * The pooled display object for one vacuum pickup entity. The renderer owns its
 * world position (snapshot-pinned while it lives); this class owns the pulse,
 * spin, and glow inside the container.
 */
export class VacuumSprite extends Container {
  /** The signature colour, exposed for the renderer's grab burst. */
  readonly color = VACUUM_COLOR;

  private readonly halo = new Graphics();
  private readonly star = new Graphics();
  private ageMs = 0;

  constructor() {
    super();
    // Soft outer halo (steady), and the starburst (pulses + spins). Drawing the
    // star once at unit scale lets the pulse ride `star.scale` - cheap per frame.
    this.halo.circle(0, 0, STAR_RADIUS * 2.6).fill({ color: VACUUM_COLOR, alpha: 0.22 });
    this.drawStar(this.star);
    this.addChild(this.halo);
    this.addChild(this.star);
    this.layout();
  }

  /** Advance the pulse/spin clock (render-frame dt, ms). */
  update(dtMs: number): void {
    this.ageMs += dtMs;
    this.layout();
  }

  private layout(): void {
    const pulse = 0.85 + 0.25 * (0.5 + 0.5 * Math.sin((this.ageMs / PULSE_PERIOD_MS) * Math.PI * 2));
    this.star.scale.set(pulse);
    this.star.rotation = (this.ageMs / SPIN_PERIOD_MS) * Math.PI * 2;
    this.halo.alpha = 0.18 + 0.12 * (0.5 + 0.5 * Math.sin((this.ageMs / PULSE_PERIOD_MS) * Math.PI * 2));
  }

  /** Draw the multi-point starburst: alternating outer/inner vertices. */
  private drawStar(g: Graphics): void {
    const inner = STAR_RADIUS * 0.42;
    for (let i = 0; i < STAR_POINTS * 2; i++) {
      const r = i % 2 === 0 ? STAR_RADIUS : inner;
      const a = (i / (STAR_POINTS * 2)) * Math.PI * 2 - Math.PI / 2;
      const x = Math.cos(a) * r;
      const y = Math.sin(a) * r * 0.6; // squashed to the isometric ground plane
      if (i === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.closePath().fill({ color: VACUUM_COLOR, alpha: 0.95 });
    // A bright core so it glints even against a busy field.
    g.circle(0, 0, inner * 0.9).fill({ color: 0xffffff, alpha: 0.95 });
  }

  dispose(): void {
    this.halo.destroy();
    this.star.destroy();
    this.destroy({ children: false });
  }
}
